import { Button, ButtonProps, Checkbox, Divider, Row, Space } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

import * as S from './GenericExportModal.styled';

export type IExportColumn = {
  key: string;
  label: string;
  checked: boolean;
};

export interface IGenericExportModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onExport: (selectedColumns: string[]) => void;
  columns: IExportColumn[];
  title?: string;
  exportButtonText?: string;
  cancelButtonText?: string;
  exportButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
}

export default function GenericExportModal({
  isOpen,
  onCancel,
  onExport,
  columns,
  title = 'Exportar para Excel',
  exportButtonText = 'Exportar arquivo',
  cancelButtonText = 'Cancelar',
  exportButtonProps = {},
  cancelButtonProps = {},
}: IGenericExportModalProps) {
  const [exportColumns, setExportColumns] = useState<IExportColumn[]>(columns);
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  // o estado interno é montado uma única vez; sem re-sincronizar na abertura o
  // modal reabre ignorando a seleção atual de colunas. A leitura é por ref para
  // não desfazer as marcações do usuário a cada render com o modal aberto.
  useEffect(() => {
    if (isOpen) setExportColumns(columnsRef.current);
  }, [isOpen]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleExport = useCallback(() => {
    const selectedColumns = exportColumns
      .filter(column => column.checked)
      .map(column => column.key);

    onExport(selectedColumns);
  }, [exportColumns, onExport]);

  const handleToggleColumn = useCallback((index: number) => {
    setExportColumns(prevColumns => {
      const newColumns = [...prevColumns];
      newColumns[index] = {
        ...newColumns[index],
        checked: !newColumns[index].checked,
      };
      return newColumns;
    });
  }, []);

  const handleToggleAll = useCallback((checked: boolean) => {
    setExportColumns(prevColumns =>
      prevColumns.map(column => ({
        ...column,
        checked,
      })),
    );
  }, []);

  const allChecked = exportColumns.every(column => column.checked);
  const someChecked = exportColumns.some(column => column.checked);

  return (
    <S.ModalContainer
      title=""
      width={550}
      open={isOpen}
      onCancel={handleCancel}
      maskClosable={false}
      footer={null}
      centered
    >
      <S.Body>
        <S.Title>{title}</S.Title>

        <Checkbox
          checked={allChecked}
          indeterminate={someChecked && !allChecked}
          onChange={e => handleToggleAll(e.target.checked)}
        >
          Selecionar todos
        </Checkbox>

        <Divider style={{ borderTop: '1px solid #e8e8e8', margin: '12px 0' }} />

        <S.ColumnList>
          {exportColumns.map((column, index) => (
            <S.ColumnItem key={column.key}>
              <Checkbox checked={column.checked} onChange={() => handleToggleColumn(index)}>
                {column.label}
              </Checkbox>
            </S.ColumnItem>
          ))}
        </S.ColumnList>
      </S.Body>

      <S.Footer>
        <Row justify="end">
          <Space size={8}>
            <Button size="large" type="ghost" onClick={handleCancel} {...cancelButtonProps}>
              {cancelButtonText}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleExport}
              disabled={!someChecked}
              {...exportButtonProps}
            >
              {exportButtonText}
            </Button>
          </Space>
        </Row>
      </S.Footer>
    </S.ModalContainer>
  );
}
